/**
 * Operacoes da propria conta: trocar senha, listar e encerrar sessoes, sair.
 *
 * Vive fora da loja de propósito. Tudo aqui e HTTP pontual com resposta que a
 * tela precisa esperar (deu certo? qual foi o erro?), enquanto a loja fala por
 * Socket.IO e expoe acoes que nao devolvem nada. Enfiar estas chamadas la
 * dentro obrigaria a inventar estado de "carregando" e "falhou" para cada uma,
 * no meio de 2000 linhas que tratam de outra coisa.
 *
 * O token e lido do `localStorage` na hora do uso, no mesmo lugar em que a
 * tela de entrada o gravou. Quando o servidor devolve um token novo (trocar
 * senha e encerrar as outras sessoes invalidam o antigo), ele e gravado aqui —
 * e quem chamou avisa a loja com `applySession`, para o socket reconectar com
 * o token certo.
 */

import { SESSION_STORAGE_KEY } from "@shared/protocol";
import type { AuthResult, SessionsResult } from "@shared/types";

export type AccountResult<T> = { ok: true; data: T } | { ok: false; message: string };

function readToken(): string | null {
  try {
    return window.localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string): void {
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, token);
  } catch {
    // Navegacao privada: a sessao segue valendo so nesta aba.
  }
}

/** Mensagem de erro do servidor (`{ error }`), quando ele explicou o que houve. */
function serverMessage(payload: unknown): string | null {
  if (typeof payload !== "object" || payload === null) return null;
  const message = (payload as Record<string, unknown>).error;
  return typeof message === "string" && message.trim() ? message : null;
}

function fallback(status: number): string {
  if (status === 401) return "Sua sessão expirou. Entre novamente.";
  if (status === 403) return "A senha atual está incorreta.";
  if (status === 429) return "Muitas tentativas seguidas. Espere um pouco e tente de novo.";
  return `Não foi possível concluir agora (erro ${status}).`;
}

async function call<T>(path: string, init?: RequestInit): Promise<AccountResult<T>> {
  const token = readToken();
  if (token === null) return { ok: false, message: "Sua sessão expirou. Entre novamente." };

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        ...(init?.body === undefined ? {} : { "Content-Type": "application/json" }),
        ...init?.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  } catch {
    return { ok: false, message: "Sem resposta do servidor. Verifique se o LFRCORD está no ar." };
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    return { ok: false, message: serverMessage(payload) ?? fallback(response.status) };
  }
  return { ok: true, data: payload as T };
}

/**
 * Troca a senha. O servidor derruba os OUTROS aparelhos e devolve um token
 * novo — o antigo acabou de virar invalido, inclusive o que esta aqui.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<AccountResult<AuthResult>> {
  const result = await call<AuthResult>("/api/account/password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (result.ok) writeToken(result.data.sessionToken);
  return result;
}

export function fetchSessions(): Promise<AccountResult<SessionsResult>> {
  return call<SessionsResult>("/api/account/sessions");
}

/** Encerra UM aparelho. A lista atualizada volta na resposta. */
export function revokeSession(sessionId: string): Promise<AccountResult<SessionsResult>> {
  return call<SessionsResult>("/api/account/sessions/revoke", {
    method: "POST",
    body: JSON.stringify({ sessionId }),
  });
}

/** Encerra todos os outros aparelhos; devolve token novo, igual a troca de senha. */
export async function revokeOtherSessions(): Promise<AccountResult<AuthResult>> {
  const result = await call<AuthResult>("/api/account/sessions/revoke", {
    method: "POST",
    body: JSON.stringify({}),
  });
  if (result.ok) writeToken(result.data.sessionToken);
  return result;
}

/**
 * Encerra a sessao DESTE aparelho no servidor. Nao lanca: sair da conta nao
 * pode travar porque a rede falhou — o cliente apaga o token de qualquer jeito,
 * e o pior caso e um token que continua valido ate vencer.
 */
export async function endSessionOnServer(): Promise<void> {
  const token = readToken();
  if (token === null) return;
  try {
    await fetch("/api/account/logout", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    });
  } catch {
    // Sem rede: o token some da maquina de qualquer forma.
  }
}
